"use client";

import { Cancel01Icon, Download01Icon, InformationCircleIcon, Share01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import { IOSInstallSheet } from "@/components/blocks/IOSInstallSheet";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { usePwaInstall } from "@/hooks/use-pwa-install";
import { cn } from "@/lib/utils";
type InstallAppBannerProps = {
  /** True when the layout renders MobileBottomNav below the banner.
   * Adds extra bottom padding so the banner clears the nav. */
  hasBottomNav?: boolean;
};
export function InstallAppBanner({
  hasBottomNav = false
}: InstallAppBannerProps = {}) {
  const {
    support,
    shouldShowNudge,
    canPromptNatively,
    promptInstall,
    dismiss,
    isIOS
  } = usePwaInstall();
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const searchParams = useSearchParams();
  const isTestMode = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (searchParams?.get("test") === "1") {
      window.sessionStorage.setItem("clutch:test-mode", "1");
      return true;
    }
    return window.sessionStorage.getItem("clutch:test-mode") === "1";
  }, [searchParams]);

  // Flip to true to force-render the banner for layout/preview work.
  const forceShowInstallUi = false;
  if (isTestMode) return null;
  if (!(shouldShowNudge || forceShowInstallUi)) return null;
  const isIOSFlow = support === "ios-safari";
  const isUnsupported = support === "unsupported";
  const showSupportingContent = isUnsupported;
  const showPrimaryAction = forceShowInstallUi || !isUnsupported;
  const disablePrimaryAction = busy || !forceShowInstallUi && !isIOSFlow && !canPromptNatively;
  const title = "Install Clutch";
  const subtitle = isUnsupported ? isIOS ? "Open this page in Safari to install Clutch." : "Use Chrome or Edge to install Clutch." : isIOSFlow ? "Add Clutch to your home screen." : "Install for a smoother experience.";
  const handlePrimary = async () => {
    if (isIOSFlow) {
      setIosSheetOpen(true);
      return;
    }
    if (!canPromptNatively) {
      return;
    }
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
    }
  };
  return <>
			<section className={cn("fixed right-0 bottom-0 left-0 z-40 print:hidden", hasBottomNav ? "pb-[calc(env(safe-area-inset-bottom)+4.5rem)] md:pb-4" : "pb-[calc(env(safe-area-inset-bottom)+1rem)] md:pb-4", "md:right-4 md:bottom-4 md:left-auto md:w-[26rem] md:max-w-[26rem]", "pointer-events-none")} aria-label="Install Clutch app">
				<Card size="sm" className={cn("pointer-events-auto mx-3 border border-border/70 bg-background/95 shadow-md backdrop-blur supports-[backdrop-filter]:bg-background/85", "md:mx-0 md:w-full")}>
					<CardHeader className="gap-0">
						<div className="flex items-center gap-3">
							<div className="shrink-0 rounded-2xl border border-border/70 bg-muted/40 p-1">
								<Image src="/favicon/web-app-manifest-192x192.png" alt="Clutch app icon" width={48} height={48} className="rounded-xl" />
							</div>

							<div className="min-w-0 flex-1 space-y-1">
								<div className="space-y-1">
									<CardTitle className="text-base leading-tight">
										{title}
									</CardTitle>
									<CardDescription className="text-sm leading-relaxed">
										{subtitle}
									</CardDescription>
								</div>
							</div>

							<Button type="button" size="icon-sm" variant="ghost" onClick={() => dismiss(false)} aria-label="Dismiss install prompt" className="shrink-0 self-start">
								<HugeiconsIcon icon={Cancel01Icon} size={18} />
							</Button>
						</div>
					</CardHeader>

					{showSupportingContent ? <CardContent className="space-y-4 pt-0">
							<Alert className="border-border/70 bg-muted/40">
								<HugeiconsIcon icon={InformationCircleIcon} size={16} />
								<AlertTitle>Install path</AlertTitle>
								<AlertDescription>
									{isIOS ? "Open this page in Safari, then use the Share menu to install it." : "Use Chrome or Edge on desktop or Android to get the native install prompt."}
								</AlertDescription>
							</Alert>
						</CardContent> : null}

					<Separator className="bg-border/70" />

					<CardFooter className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
						{showPrimaryAction ? <Button size="lg" onClick={handlePrimary} disabled={disablePrimaryAction} className="w-full">
								<HugeiconsIcon icon={isIOSFlow ? Share01Icon : Download01Icon} size={16} />
								{isIOSFlow ? "How to install" : "Install app"}
							</Button> : null}
					</CardFooter>
				</Card>
			</section>

			<IOSInstallSheet open={iosSheetOpen} onOpenChange={setIosSheetOpen} />
		</>;
}